/**
 * 이사 유형 스펙 — 서버 응답 모양과 코드 폴백 라벨.
 *
 * 앱과 문서 런타임이 같은 모양을 봐야 한다. 한쪽만 필드를 알면 유형 카드는 뜨는데
 * 눌러도 질문이 안 나오는 식으로 조용히 어긋난다.
 */

/** DynamicAttributeType — Jackson 다형: 문자열 또는 객체. */
export type DynamicAttributeTypeJson = string | Record<string, unknown>;

export interface MovingTypeSpecificationAttribute {
  key: string;
  name: string;
  type: DynamicAttributeTypeJson;
  optional: boolean;
  fieldKind: string;
  helpText: string | null;
  enumValues: string[] | null;
}

export interface MovingTypeSpecificationResponse {
  code: string;
  name: string;
  helpText: string | null;
  iconDownloadUrl: string | null;
  attributes: MovingTypeSpecificationAttribute[];
}

export interface MovingTypeIntakeView {
  selectedTypes: string[];
  attributesByType: Record<string, Record<string, string>>;
}

export interface RecordMovingTypeIntakeRequest {
  selectedTypes: string[];
  attributesByType: Record<string, Record<string, string>>;
}

/**
 * 스펙 조회 전·실패 시에도 코드(`DELAYED`)가 그대로 노출되지 않게 하는 폴백.
 * 스펙의 `name` 이 있으면 언제나 그쪽이 우선이다.
 */
export const MOVING_TYPE_CODE_FALLBACK_LABELS: Record<string, string> = {
  DELAYED: "대기이사",
  STORAGE: "보관이사",
  PARTIAL: "부분이사",
  OFFICE: "사무실이사",
  JEJU: "제주이사",
  STOPOVER: "경유이사",
  LONG_DISTANCE: "장거리이사",
  SPLIT: "분리이사",
  MERGE: "합침이사",
};

/** 아이콘이 안 내려올 때 쓰는 이모지. 카드가 빈 사각형으로 남지 않게 한다. */
export const MOVING_TYPE_EMOJI: Record<string, string> = {
  DELAYED: "⏰",
  STORAGE: "📦",
  STOPOVER: "🔀",
  PARTIAL: "🧳",
  LONG_DISTANCE: "🚛",
  OFFICE: "🏢",
  SPLIT: "🏘️",
  MERGE: "🤝",
  JEJU: "✈️",
};

/** 선택된 이사 유형 코드를 사람이 읽는 문자열로. */
export function formatMovingTypeSelectedTypesForDisplay(
  selectedTypes: string[] | null | undefined,
  specs?: MovingTypeSpecificationResponse[] | null,
): string | undefined {
  if (!selectedTypes?.length) return undefined;
  const byCode = new Map((specs ?? []).map((s) => [s.code, s]));
  return selectedTypes
    .map((code) => {
      const name = byCode.get(code)?.name?.trim();
      if (name) return name;
      return MOVING_TYPE_CODE_FALLBACK_LABELS[code] ?? code;
    })
    .join(", ");
}
